import { combineReducers } from 'redux';
import { takeLatest } from 'redux-saga/effects';

import {
    UserGroupDetailsPayload,
    UserGroupListPayload,
    details as detailsApiCall,
    list as listApiCall,
} from '../api/userGroups';
import { RequestState, MainReducerState } from '../reducers';
import { ListResponse, UserGroup } from '../api/apiTypes';
import { EzeeAsyncAction } from '../helpers/EzeeAsyncAction';
import { simpleAsyncSaga } from '../helpers/EzeeSaga';

// State

export interface UserGroupsState {
    list: RequestState<ListResponse<UserGroup>>;
    search: RequestState<ListResponse<UserGroup>>;
    details: RequestState<UserGroup | undefined>;
}

const initialState: UserGroupsState = {
    list: {
        data: {
            items: [],
            totalCount: 0,
            page: 0,
            pageSize: 20,
            pageCount: 0,
        },
        loading: false,
        success: false,
    },
    search: {
        data: {
            items: [],
            totalCount: 0,
            page: 0,
            pageSize: 20,
            pageCount: 0,
        },
        loading: false,
        success: false,
    },
    details: {
        data: undefined,
        loading: false,
        success: false,
    },
};

// Actions/Reducers

export const list = new EzeeAsyncAction<
    UserGroupsState['list'],
    UserGroupListPayload,
    ListResponse<UserGroup>
>('userGroups/list', initialState.list, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.list,
    }),
});

export const search = new EzeeAsyncAction<
    UserGroupsState['search'],
    UserGroupListPayload,
    ListResponse<UserGroup>
>('userGroups/search', initialState.search, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.search,
    }),
});

export const details = new EzeeAsyncAction<
    UserGroupsState['details'],
    UserGroupDetailsPayload,
    UserGroup
>('userGroups/details', initialState.details, {
    trigger: (state, payload) => ({
        ...state,
        loading: true,
    }),
    success: (state, payload) => ({
        data: payload,
        loading: false,
    }),
    failure: (state, payload) => ({
        ...state,
        loading: false,
        error: payload,
    }),
    reset: (state) => ({
        ...initialState.details,
    }),
});

// Reducer

export const userGroupsReducer = combineReducers<UserGroupsState>({
    details: details.reducer,
    list: list.reducer,
    search: search.reducer,
});

// Saga

export function* userGroupsSaga() {
    yield takeLatest(list.type.trigger, simpleAsyncSaga(listApiCall, list));
    yield takeLatest(search.type.trigger, simpleAsyncSaga(listApiCall, search));
    yield takeLatest(details.type.trigger, simpleAsyncSaga(detailsApiCall, details));
}

// Store helpers

export const getUserGroupsState = (state: MainReducerState) => state.userGroups;
export const getUserGroupsListState = (state: MainReducerState) => state.userGroups.list;
export const getUserGroupsSearchState = (state: MainReducerState) => state.userGroups.search;
